import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import TableSelector from './TableSelector';
import DataTable from './DataTable';
import FilterPanel from './FilterPanel';
import ConnectionChecker from './ConnectionChecker';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiDatabase, FiTable, FiFilter, FiRefreshCw, FiAlertCircle, FiLock } = FiIcons;

const DatabaseVisualizer = () => {
  const location = useLocation();
  const [selectedDatabase, setSelectedDatabase] = useState('');
  const [selectedTable, setSelectedTable] = useState('');
  const [databases, setDatabases] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConnectionChecker, setShowConnectionChecker] = useState(false);
  const [serverStatus, setServerStatus] = useState('checking'); // 'checking', 'online', 'offline'
  const [uuid, setUuid] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [tableCache, setTableCache] = useState({});

  // Pre-fetch and cache tables for better performance
  const fetchTables = useCallback(async (database) => {
    if (!database || !uuid) return;
    
    // Check if we have a cached version first
    const cacheKey = `${database}_tables`;
    if (tableCache[cacheKey] && tableCache[cacheKey].timestamp > Date.now() - 60000) { // 1 minute cache
      console.log("Using cached tables data");
      setTables(tableCache[cacheKey].data);
      setSelectedTable('');
      return;
    }
    
    try {
      setLoading(true);
      // IMPORTANT: Always include the UUID parameter for authentication
      const url = `/api/tables/${encodeURIComponent(database)}?uuid=${encodeURIComponent(uuid)}`;
      console.log("Fetching tables from URL:", url);
      
      const response = await fetch(url, {
        headers: {
          'Cache-Control': 'no-cache', // Prevent caching for fresh data
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.details || 'Failed to fetch tables');
      }
      
      const data = await response.json();
      setTables(data);
      setSelectedTable('');
      
      // Cache the tables data
      setTableCache(prev => ({
        ...prev,
        [cacheKey]: {
          data,
          timestamp: Date.now()
        }
      }));
    } catch (err) {
      console.error("Tables fetch error:", err);
      if (err.message.includes('Access denied') || err.message.includes('Invalid UUID')) {
        setError('Authentication failed');
      } else {
        setError('Failed to fetch tables');
      }
    } finally {
      setLoading(false);
    }
  }, [uuid, tableCache]);

  const checkServerStatus = useCallback(async (dbParam, uuidParam) => {
    try {
      setServerStatus('checking');
      console.log(`Checking server with database: ${dbParam}, UUID: ${uuidParam ? uuidParam.substring(0, 4) + '****' : 'missing'}`);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000); // Reduced timeout for faster response
      
      // Check server health first with timeout
      await fetch(`/api/health`, {
        signal: controller.signal,
        headers: {
          'Cache-Control': 'no-cache',
        }
      });
      
      clearTimeout(timeoutId);
      setServerStatus('online');
      console.log("Server is online. Using database:", dbParam, "and UUID:", uuidParam ? uuidParam.substring(0, 4) + '****' : 'missing');
    } catch (err) {
      console.error('Server connection error:', err);
      setServerStatus('offline');
      setError('Server connection error');
    }
  }, []);

  const fetchDatabases = useCallback(async () => {
    if (!selectedDatabase || !uuid) return;
    
    try {
      setLoading(true);
      console.log("Fetching database access for:", selectedDatabase, "with UUID:", uuid ? uuid.substring(0, 4) + '****' : 'missing');
      
      // Include UUID in the request
      const url = `/api/databases?database=${encodeURIComponent(selectedDatabase)}&uuid=${encodeURIComponent(uuid)}`;
      console.log("Fetching from URL:", url);
      
      const response = await fetch(url, {
        headers: {
          'Cache-Control': 'no-cache',
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.details || 'Failed to fetch database');
      }
      
      const data = await response.json();
      setDatabases(data);
      console.log("Authorized databases:", data);
      setInitialized(true); // Set initialized to true after successful database fetch
      
      // Immediately fetch tables after database is verified
      if (data.length > 0) {
        fetchTables(selectedDatabase);
      }
    } catch (err) {
      console.error("Database fetch error:", err);
      if (err.message.includes('Access denied') || err.message.includes('Invalid UUID')) {
        setError('Authentication failed');
      } else if (err.message.includes('not found')) {
        setError('Database not found');
      } else {
        setError('Connection error');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedDatabase, uuid, fetchTables]);

  // Extract parameters from hash part only (for HashRouter)
  useEffect(() => {
    const extractParamsFromHash = () => {
      const hashParts = window.location.hash.split('?');
      if (hashParts.length > 1) {
        const hashParams = new URLSearchParams(hashParts[1]);
        const dbParam = hashParams.get('database');
        const uuidParam = hashParams.get('uuid');
        console.log("Extracted parameters from hash:", { database: dbParam, uuid: uuidParam ? `${uuidParam.substring(0, 4)}****` : null });
        return { dbParam, uuidParam };
      }
      return { dbParam: null, uuidParam: null };
    };

    // Get parameters only from hash
    const { dbParam, uuidParam } = extractParamsFromHash();

    // Validate required URL parameters
    if (!dbParam) {
      setError('Authentication required');
    } else if (!uuidParam) {
      setError('Authentication required');
    } else {
      // Set the parameters we found
      setSelectedDatabase(dbParam);
      setUuid(uuidParam);
      
      // Parameters present, proceed with connection check
      checkServerStatus(dbParam, uuidParam);
    }
  }, [location, checkServerStatus]);

  // Only fetch databases when necessary
  useEffect(() => {
    if (serverStatus === 'online' && selectedDatabase && uuid) {
      fetchDatabases();
    }
  }, [serverStatus, selectedDatabase, uuid, fetchDatabases]);

  const handleDatabaseChange = (database) => {
    setSelectedDatabase(database);
    setSelectedTable('');
  };

  const handleRefresh = () => {
    if (serverStatus !== 'online') {
      // Extract parameters from hash part only
      const hashParts = window.location.hash.split('?');
      if (hashParts.length > 1) {
        const hashParams = new URLSearchParams(hashParts[1]);
        const dbParam = hashParams.get('database');
        const uuidParam = hashParams.get('uuid');
        if (dbParam && uuidParam) {
          checkServerStatus(dbParam, uuidParam);
        }
      }
      return;
    }

    if (selectedDatabase) {
      // Clear cache for this database to force refresh
      const cacheKey = `${selectedDatabase}_tables`;
      setTableCache(prev => {
        const newCache = {...prev};
        delete newCache[cacheKey];
        return newCache;
      });
      fetchTables(selectedDatabase);
    } else {
      fetchDatabases();
    }
  };

  // Authentication error message - Clean version
  if (error && (error.includes('Authentication') || error.includes('Database not found'))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto bg-white p-8 rounded-lg shadow-lg">
          <div className="text-red-500 text-6xl mb-6">
            <SafeIcon icon={FiLock} />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Authentication Error</h2>
          <p className="text-gray-700 mb-6">
            Unable to access the database. Please verify your credentials and try again.
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (serverStatus === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin text-blue-500 text-6xl mb-4">⟳</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Connecting to Server</h2>
          <p className="text-gray-600 mb-4">Please wait...</p>
        </div>
      </div>
    );
  }

  if (serverStatus === 'offline') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto bg-white p-8 rounded-lg shadow-lg">
          <div className="text-red-500 text-6xl mb-6">
            <SafeIcon icon={FiAlertCircle} />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Server Not Available</h2>
          <p className="text-gray-700 mb-6">
            The database server is currently unavailable. Please try again later.
          </p>
          <button 
            onClick={handleRefresh} 
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  if (error && error.includes('Connection error')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto bg-white p-8 rounded-lg shadow-lg">
          <div className="text-red-500 text-6xl mb-6">
            <SafeIcon icon={FiDatabase} />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Connection Error</h2>
          <p className="text-gray-700 mb-6">
            Unable to connect to the database. Please try again later.
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white shadow-lg border-b border-gray-200"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <img src="https://img.perceptpixel.com/ktmrrvjw/ncb.png" alt="NCB Logo" className="h-8" />
            </div>
            <div className="flex items-center space-x-4">
              {selectedDatabase && (
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <SafeIcon icon={FiDatabase} className="text-blue-500" />
                  <span className="font-medium">{selectedDatabase}</span>
                  {selectedTable && (
                    <>
                      <span>/</span>
                      <SafeIcon icon={FiTable} className="text-green-500" />
                      <span className="font-medium">{selectedTable}</span>
                    </>
                  )}
                </div>
              )}
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="p-2 text-gray-600 hover:text-blue-600 transition-colors disabled:opacity-50"
              >
                <SafeIcon icon={FiRefreshCw} className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className="lg:col-span-1"
          >
            <TableSelector
              databases={databases}
              tables={tables}
              selectedDatabase={selectedDatabase}
              selectedTable={selectedTable}
              onDatabaseChange={handleDatabaseChange}
              onTableChange={setSelectedTable}
              loading={loading}
              readOnly={true} // Database selection is now read-only since it's from URL
            />
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className="lg:col-span-3"
          >
            {selectedDatabase && selectedTable ? (
              <DataTable
                database={selectedDatabase}
                table={selectedTable}
                uuid={uuid} // Pass UUID to data table for API calls
              />
            ) : (
              <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                <div className="text-gray-400 text-6xl mb-4">
                  <SafeIcon icon={FiTable} />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Select a Table to View Data
                </h3>
                <p className="text-gray-600">
                  Choose a table from the sidebar to start visualizing your data.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Connection Checker Modal */}
      <AnimatePresence>
        {showConnectionChecker && (
          <ConnectionChecker onClose={() => setShowConnectionChecker(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default DatabaseVisualizer;